import addSeconds from 'date-fns/addSeconds';
import isBefore from 'date-fns/isBefore';
import subSeconds from 'date-fns/subSeconds';
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping';
import isWithinInterval from 'date-fns/isWithinInterval';
import cloneDeep from 'lodash/cloneDeep';
import forOwn from 'lodash/forOwn';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import mapValues from 'lodash/mapValues';
import update from 'lodash/update';
import every from 'lodash/every';
import { v4 as uuidv4 } from 'uuid';
import * as Constants from '../data/Constants';

const HOUR_TYPES = Object.freeze({
  business: 'business',
  delivery: 'delivery',
  pickup: 'pickup',
});

const HOUR_TYPES_ARRAY = Object.values(HOUR_TYPES);
const SEVEN_DAYS_IN_WEEK = Object.keys(Constants.sevenDaysInAWeek);

const HOUR_SETTINGS = Object.freeze({
  [HOUR_TYPES.business]: {
    closed: 'businessClosed',
    close: 'close',
    open: 'open',
    openAllDay: 'businessOpenAllDay',
    label: 'Opening Hours',
  },
  [HOUR_TYPES.delivery]: {
    closed: 'deliveriesClosed',
    close: 'deliveryClose',
    open: 'deliveryOpen',
    openAllDay: 'deliveryOpenAllDay',
    label: 'Delivery Hours',
  },
  [HOUR_TYPES.pickup]: {
    closed: 'pickupsClosed',
    close: 'pickupClose',
    open: 'pickupOpen',
    openAllDay: 'pickupOpenAllDay',
    label: 'Pickup Hours',
  },
});

/**
 * Copies of this are used to make new hours records when existing hours records have no room
 * to merge new hours info for a delivery option onto.
 * Undefined becomes NULL in the database after sending to the API.
 * Undefined is used as an intermediate state
 * in the Admin panel to signal a new hours record that
 * still has room for info in a delivery option.
 *
 * Because we are seeing issues for pickupsClosed: NULL in Web/Mobile's UI showing
 * a location's opening hours when rejecting a desiredTime choice for an order,
 * we'll make pickupsClosed false instead by default.
 */
const BASE_HOUR_RECORD = Object.freeze({
  [HOUR_SETTINGS[HOUR_TYPES.business].closed]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.business].open]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.business].close]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.business].openAllDay]: false,
  [HOUR_SETTINGS[HOUR_TYPES.delivery].closed]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.delivery].open]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.delivery].close]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.delivery].openAllDay]: false,
  [HOUR_SETTINGS[HOUR_TYPES.pickup].closed]: false,
  [HOUR_SETTINGS[HOUR_TYPES.pickup].open]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.pickup].close]: undefined,
  [HOUR_SETTINGS[HOUR_TYPES.pickup].openAllDay]: false,
});

const HOUR_VALID_FORMS = Object.freeze({
  isValidOpenAllDayValues: 'isValidOpenAllDayValues',
  isValidClosedValues: 'isValidClosedValues',
  tempRecordDoesNotApplyToThisTab: 'tempRecordDoesNotApplyToThisTab',
  isUnusedOrRemovedHour: 'isUnusedOrRemovedHour',
});

const HOUR_ERROR_TYPES = Object.freeze({
  isOpenAndIsMissingTimes: 'isOpenAndIsMissingTimes',
  isBothClosedAndHasTimes: 'isBothClosedAndHasTimes',
  isClosedAndOpenAllDay: 'isClosedAndOpenAllDay',
  isBothClosedUnsetAndHasOneTime: 'isBothClosedUnsetAndHasOneTime',
});

const initializeHours = (hours) => {
  const updatedHours = hours.map((hourRecord) => {
    let recordCopy = cloneDeep(hourRecord);
    HOUR_TYPES_ARRAY.forEach((hourType) => {
      const hourSettingsKeys = HOUR_SETTINGS[hourType];
      recordCopy = setIsOpenIfBothTimesPresent(recordCopy, hourSettingsKeys);
    });
    return recordCopy;
  });
  return updatedHours;
};

const isHourRecordForDeliveryOption = (hourRecord, hourSettingsKeys) => {
  const { open, close } = hourSettingsKeys;
  const openingTime = hourRecord[open];
  const closingTime = hourRecord[close];

  const isOpenTimeUndefined = isUndefined(openingTime);
  const isClosingTimeUndefined = isUndefined(closingTime);
  return !isOpenTimeUndefined && !isClosingTimeUndefined;
};

/**
 * New hours records are only made if existing hours records do not have room to store
 * new info on a delivery option. They are made from BASE_HOUR_RECORD.
 */
const isNewRecordDeliveryOptionOverwriteable = (hourRecord, hourType) => {
  const hourSettingsKeys = HOUR_SETTINGS[hourType];
  const { closed, openAllDay } = hourSettingsKeys;
  const { open, close } = hourSettingsKeys;
  const { [closed]: isClosed, [openAllDay]: isOpenAllDay } = hourRecord;
  const { [open]: openingTime, [close]: closingTime } = hourRecord;

  const isOverwriteableArgs = {
    isClosed,
    isOpenAllDay,
    openingTime,
    closingTime,
  };
  const isOverwriteableFunc = hourType === HOUR_TYPES.pickup
    ? isNewPickupHoursRecordOverwriteable : isNewNonPickupHoursRecordOverwriteable;
  return isOverwriteableFunc(isOverwriteableArgs);
};

const isNewNonPickupHoursRecordOverwriteable = (args) => {
  const { isClosed, isOpenAllDay } = args;
  const { openingTime, closingTime } = args;
  return (
    isUndefined(isClosed)
    && isUndefined(openingTime)
    && isUndefined(closingTime)
    && !isOpenAllDay
  );
};

const isNewPickupHoursRecordOverwriteable = (args) => {
  const { isClosed, isOpenAllDay } = args;
  const { openingTime, closingTime } = args;
  return !isClosed && isUndefined(openingTime) && isUndefined(closingTime) && !isOpenAllDay;
};

const hasBothTimesPresentAndIsClosed = (hour, hourSettingsKeys) => {
  const { closed, open, close } = hourSettingsKeys;
  return hour[closed] && hour[open] && hour[close];
};

const setIsOpenIfBothTimesPresent = (recordCopy, hourSettingsKeys) => {
  if (hasBothTimesPresentAndIsClosed(recordCopy, hourSettingsKeys)) {
    const { closed } = hourSettingsKeys;
    recordCopy[closed] = false;
  }
  return recordCopy;
};

/**
 * Checks that when set, all opening and closing times have the form '8:30' or '08:30'
 */
const isHourObjectValid = (hourRecord) => {
  const isAllHourStringValuesValid = HOUR_TYPES_ARRAY.every((hourType) => {
    const hourSettingsKeys = HOUR_SETTINGS[hourType];
    const { open, close } = hourSettingsKeys;
    const isTempRecordForOtherTab = isTempRecordForAnotherTab(hourRecord, hourSettingsKeys);
    if (isTempRecordForOtherTab) return true;

    const openingTime = hourRecord[open];
    const closingTime = hourRecord[close];
    return isValidHourString(openingTime) && isValidHourString(closingTime);
  });
  return isAllHourStringValuesValid;
};

const isTempRecordForAnotherTab = (hourRecord, hourSettingsKeys) => {
  const {
    closed,
    open,
    close,
    openAllDay,
  } = hourSettingsKeys;
  const hasNoValuesYetForTab = isUndefined(hourRecord[closed]) && isUndefined(hourRecord[open])
    && isUndefined(hourRecord[close]) && isUndefined(hourRecord[openAllDay]);

  return hasNoValuesYetForTab;
};

const getLeadingZeroTimes = (hourRecord) => {
  const newHourRecord = { ...hourRecord };
  HOUR_TYPES_ARRAY.forEach((hourType) => {
    const { open, close } = HOUR_SETTINGS[hourType];
    newHourRecord[open] = getLeadingZeroTime(hourRecord[open]);
    newHourRecord[close] = getLeadingZeroTime(hourRecord[close]);
  });

  return newHourRecord;
};

/**
 * Changes 8:30 to 08:30 in hours state. Safari uses 8:30, but the database
 * and other browsers use '08:30'.
 * @param {*} timeString e.g. '08:30'
 */
const getLeadingZeroTime = (timeString) => {
  if (!isValidHourString(timeString) || timeString === null) return timeString;

  const hourArray = timeString.split(':');
  let hourNumStr = hourArray[0];
  if (parseInt(hourNumStr) <= 9 && hourNumStr.length < 2) {
    hourNumStr = '0'.concat(hourNumStr);
    const newHour = [hourNumStr, hourArray[1]].join(':');
    return newHour;
  }
  return timeString;
};

/**
 * Checks that an "hour string" value either has the form '08:30' or '8:30'
 */
const isValidHourString = (hour) => {
  if (hour === null) return true;
  if (typeof hour !== 'string') return false;

  const hourArray = hour.split(':');
  const [hours, minutes] = hourArray;
  if (hourArray.length !== 2 || isNaN(hours) || isNaN(minutes) || minutes.length !== 2) {
    return false;
  }

  const hourNum = parseInt(hours);
  const minuteNum = parseInt(minutes);
  return hourNum >= 0 && hourNum < 24 && minuteNum >= 0 && minuteNum < 60;
};

const ERROR_MESSAGE = "We're improving the hours editing!"
  + "\n\r\n\rIf you just typed a correct time and it seems the large red borders haven't updated, hit Tab, Enter, or click somewhere else."
  + "\n\r\n\rWith multiple hours a day per tab now possible, check that times inside the same tab for the same day don't overlap."
  + "\n\r\n\rIf you're open that day, un-tick the Closed checkbox and put down both the opening and closing time."
  + "\n\r\n\rIf you're closed that day, tick the Closed checkbox, and leave the hours blank."
  + "\n\r\n\rCheck both the Opening Hours and Delivery Hours tabs to make sure everything's okay!";

const isAllHourTypesValid = (hourRecord) => {
  const isAllSettingsPresencesValid = HOUR_TYPES_ARRAY.every((hourType) => {
    const hourKeysByType = HOUR_SETTINGS[hourType];
    const validationResult = isSettingsInHourValid(hourRecord, hourKeysByType);
    return validationResult;
  });

  const isStringValuesOfTimesValid = isHourObjectValid(hourRecord);
  return isAllSettingsPresencesValid && isStringValuesOfTimesValid;
};

const getIsHourValidResults = (hourRecord) => {
  const doTabsHaveErrors = [];

  const isAllSettingsPresencesValid = HOUR_TYPES_ARRAY.every((hourType) => {
    // While still hiding Pickup hours, we need to allow whatever database values exist to pass
    if (hourType === HOUR_TYPES.pickup) return true;

    const hourKeysByType = HOUR_SETTINGS[hourType];
    const hasError = !isSettingsInHourValid(hourRecord, hourKeysByType);
    doTabsHaveErrors.push([hourType, hasError]);

    return !hasError;
  });

  const isStringValuesOfTimesValid = isHourObjectValid(hourRecord);
  const isAllHourTypesValid = isAllSettingsPresencesValid && isStringValuesOfTimesValid;
  return { isAllHourTypesValid, doTabsHaveErrors };
};

const isSettingsInHourValid = (hourRecord, hourKeysByType) => {
  const validationResults = validateOneHourTypeOfOneRecord(hourRecord, hourKeysByType);
  let isValidForm = false;
  let hasError = false;
  Object.entries(validationResults).forEach(([resultName, value]) => {
    if (HOUR_VALID_FORMS[resultName]) isValidForm = returnTrueOnceOneIsTrue(isValidForm, value);
    if (HOUR_ERROR_TYPES[resultName]) hasError = returnTrueOnceOneIsTrue(hasError, value);
  });

  return isValidForm || !hasError;
};

const returnTrueOnceOneIsTrue = (acc, val) => {
  if (acc) return !!acc;
  return !!val;
};

const validateOneHourTypeOfOneRecord = (hour, hourSettingsLookupKeys) => {
  const {
    closed,
    open,
    close,
    openAllDay,
  } = hourSettingsLookupKeys;

  const isClosedForDay = hour[closed];
  const isClosedForDayNull = isClosedForDay === null;
  const isOpenAllDay = hour[openAllDay];
  const hasOpeningTime = hour[open];
  const hasClosingTime = hour[close];
  const isMissingAtLeastOneTime = !hasOpeningTime || !hasClosingTime;
  const hasAtLeastOneTime = hasOpeningTime || hasClosingTime;
  const hasOnlyOneTime = (hasOpeningTime && !hasClosingTime) || (!hasOpeningTime && hasClosingTime);
  const hasNoValuesYet = isTempRecordForAnotherTab(hour, hourSettingsLookupKeys);
  const isValidClosedValues = isClosedForDay && !hasOpeningTime && !hasClosingTime && !isOpenAllDay;
  const isOpenAndIsMissingTimes = !isClosedForDay && !isOpenAllDay && isMissingAtLeastOneTime;
  const isUnusedOrRemovedHour = isClosedForDayNull && !isOpenAllDay;
  const results = {
    [HOUR_VALID_FORMS.tempRecordDoesNotApplyToThisTab]: hasNoValuesYet,
    [HOUR_VALID_FORMS.isValidOpenAllDayValues]: !isClosedForDay && isOpenAllDay,
    [HOUR_VALID_FORMS.isValidClosedValues]: isValidClosedValues,
    [HOUR_VALID_FORMS.isUnusedOrRemovedHour]: isUnusedOrRemovedHour,
    [HOUR_ERROR_TYPES.isOpenAndIsMissingTimes]: isOpenAndIsMissingTimes,
    [HOUR_ERROR_TYPES.isBothClosedAndHasTimes]: isClosedForDay && hasAtLeastOneTime,
    [HOUR_ERROR_TYPES.isClosedAndOpenAllDay]: isClosedForDay && isOpenAllDay,
    [HOUR_ERROR_TYPES.isBothClosedUnsetAndHasOneTime]: isClosedForDayNull && hasOnlyOneTime,
  };
  return results;
};

const sortNewTempRecordsByHourType = (tempIdRecords) => {
  const results = { [HOUR_TYPES.business]: [], [HOUR_TYPES.delivery]: [], [HOUR_TYPES.pickup]: [] };
  tempIdRecords.forEach((hourRecord) => {
    HOUR_TYPES_ARRAY.forEach((hourType) => {
      const hourSettingsKeys = HOUR_SETTINGS[hourType];
      const isPartOfDeliveryOption = isHourRecordForDeliveryOption(hourRecord, hourSettingsKeys);
      if (!isPartOfDeliveryOption) return;

      results[hourType].push(hourRecord);
    });
  });

  return results;
};

const findRecordsWithSpace = ({
  existingIdRecords,
  newRecords,
  hourType,
}) => {
  const recordsWithSpaceAndExistingId = existingIdRecords.filter(
    record => isRecordOverwriteable(record, hourType),
  );
  const newRecordsWithSpace = newRecords.filter(
    record => isNewRecordDeliveryOptionOverwriteable(record, hourType),
  );
  return { recordsWithSpaceAndExistingId, newRecordsWithSpace };
};

const isHourTypeEmpty = (record, hourType) => {
  const { closed, openAllDay } = HOUR_SETTINGS[HOUR_TYPES[hourType]];
  const { close, open } = HOUR_SETTINGS[HOUR_TYPES[hourType]];
  const closedCheck = hourType === HOUR_TYPES.pickup
    ? !record[closed]
    : isNull(record[closed]);

  return closedCheck && isNull(record[open]) && isNull(record[close]) && !record[openAllDay];
};

const isRecordOverwriteable = isHourTypeEmpty;

const mergeTempRecordsIntoRecordsWithSpace = ({
  tempRecordsForHourType,
  recordsWithSpaceAndExistingId,
  newRecordsWithSpace,
  hourType,
}) => {
  if (!Array.isArray(recordsWithSpaceAndExistingId) || !Array.isArray(tempRecordsForHourType)) {
    return;
  }
  const mergedTempRecordIds = {};

  const mergeIntoExistingRecordsArgs = {
    recordsWithSpaceAndExistingId,
    tempRecordsForHourType,
    mergedTempRecordIds,
    hourType,
  };
  const updatedExistingRecordsMap = mergeTempRecordsIntoExistingRecords(mergeIntoExistingRecordsArgs);

  const updatedNewRecordsMap = mergeTempRecordsIntoNewRecords(
    tempRecordsForHourType,
    newRecordsWithSpace,
    mergedTempRecordIds,
  );
  const leftoverTempRecords = tempRecordsForHourType.filter(
    tempRecord => !mergedTempRecordIds[tempRecord.tempId]
  );

  return { updatedExistingRecordsMap, leftoverTempRecords, updatedNewRecordsMap };
};

/**
 * Side-effect: assigns to mergedTempRecordIds from call
 * site to track which temp ids don't need work
 */
const mergeTempRecordsIntoNewRecords = (
  tempRecordsForHourType,
  newRecordsWithSpace,
  mergedTempRecordIds,
) => {
  const updatedNewRecordsMap = {};
  tempRecordsForHourType.forEach((tempRecord, ind) => {
    const { tempId, ...tempRecordWithoutTempId } = tempRecord;
    const newRecordWithSpace = newRecordsWithSpace[ind];
    if (!newRecordWithSpace) return;
    const { tempId: newRecordId } = newRecordWithSpace;
    if (mergedTempRecordIds[tempId]) return;

    const newRecordAfterMerge = { ...newRecordWithSpace, ...tempRecordWithoutTempId };

    mergedTempRecordIds[tempId] = true;
    updatedNewRecordsMap[newRecordId] = newRecordAfterMerge;
  });
  return updatedNewRecordsMap;
};

/**
 * Side-effect: assigns to mergedTempRecordIds from call site to track which temp ids don't need work
 */
const mergeTempRecordsIntoExistingRecords = ({
  recordsWithSpaceAndExistingId,
  tempRecordsForHourType,
  mergedTempRecordIds,
  hourType,
}) => {
  const updatedExistingRecordsMap = {};
  const emptyTempRecords = tempRecordsForHourType.filter(
    tempRecord => isHourTypeEmpty(tempRecord, hourType),
  );
  const tempRecordsWithInfo = tempRecordsForHourType.filter(
    tempRecord => !isHourTypeEmpty(tempRecord, hourType),
  );
  // Track emptyTempRecords to skip merging them
  emptyTempRecords.forEach(({ tempId }) => mergedTempRecordIds[tempId] = true);

  recordsWithSpaceAndExistingId.forEach((existingRecord, ind) => {
    const tempRecord = tempRecordsWithInfo[ind];

    // If there's less new temp records than existing records with space
    if (!tempRecord) {
      const { id } = existingRecord;
      updatedExistingRecordsMap[id] = existingRecord;
      return;
    }
    const { tempId, ...tempRecordWithoutTempId } = tempRecord;
    if (mergedTempRecordIds[tempId]) return;

    const { id } = existingRecord;

    const mergedRecord = { ...existingRecord, ...tempRecordWithoutTempId };

    mergedTempRecordIds[tempId] = true;
    updatedExistingRecordsMap[id] = mergedRecord;
  });
  return updatedExistingRecordsMap;
};

const makeNewRecordsFromLastTempRecords = (leftoverTempRecords) => {
  const newRecordsFromTempRecords = leftoverTempRecords.map((tempRecord) => {
    const newRecord = { ...BASE_HOUR_RECORD, ...tempRecord };
    return newRecord;
  });

  return newRecordsFromTempRecords;
};

const collectUnchangedAndUpdatedExistingRecords = (
  existingIdRecords,
  updatedExistingRecordsMap,
) => {
  const withUpdatedRecords = existingIdRecords.map((existingRecord) => {
    const { id } = existingRecord;
    const updatedRecord = updatedExistingRecordsMap[id];
    return updatedRecord || existingRecord;
  });
  return withUpdatedRecords;
};

const collectUnchangedAndUpdatedNewRecords = (
  newRecords,
  updatedNewRecordsMap,
  newRecordsFromLeftoverTempRecords,
) => {
  const withUpdatedRecords = newRecords.map((newRecord) => {
    const { tempId } = newRecord;
    const updatedRecord = updatedNewRecordsMap[tempId];
    return updatedRecord || newRecord;
  });
  return [...withUpdatedRecords, ...newRecordsFromLeftoverTempRecords];
};

const cleanKeysFromFinalHours = (finalHours) => {
  const cleanedHours = finalHours.map((hourRecord) => {
    const { tempId, ...cleanedRecord } = hourRecord;
    return cleanedRecord;
  });

  return cleanedHours;
};


const removeCompletelyEmptyRecords = (hours) => {
  const filtered = hours.filter((hour) => {
    const isHourTypesEmpty = HOUR_TYPES_ARRAY.map(hourType => isHourTypeEmpty(hour, hourType));
    return !every(isHourTypesEmpty);
  });
  return filtered;
};

const setOpenWhenBothTimesPresent = (hours) => {
  const fixedHours = hours.map((hour) => {
    let hourClone = { ...hour };
    HOUR_TYPES_ARRAY.forEach((hourType) => {
      const hourSettingsKeys = HOUR_SETTINGS[hourType];
      hourClone = setIsOpenIfBothTimesPresent(hourClone, hourSettingsKeys);
    });

    return hourClone;
  });

  return fixedHours;
};

const consolidateHoursRecords = (hours) => {
  let finalHours = [];

  const hoursClone = setBusinessHoursOnDeliveryOption(
    hours,
    HOUR_TYPES.pickup,
    HOUR_TYPES.delivery,
  );

  SEVEN_DAYS_IN_WEEK.forEach((day) => {
    const sameDayRecords = hoursClone.filter(hour => hour.day === day);
    const tempIdRecords = sameDayRecords.filter(hour => !!hour.tempId && !hour.id);
    let existingIdRecords = sameDayRecords.filter(hour => !!hour.id && !hour.tempId);
    const tempRecordsByHourType = sortNewTempRecordsByHourType(tempIdRecords);

    let newRecords = [];

    HOUR_TYPES_ARRAY.forEach((hourType) => {
      ({ existingIdRecords, newRecords } = putTempRecordInfoIntoLeastRecordsPossible(
        hourType, existingIdRecords, newRecords, tempRecordsByHourType,
      ));
    });

    finalHours = [...finalHours, ...existingIdRecords, ...newRecords];
  });

  finalHours = cleanKeysFromFinalHours(finalHours);
  finalHours = removeCompletelyEmptyRecords(finalHours);

  // At this point, all temp record info has been put into existing records with space,
  // or new records with no id, but all business/delivery/pickup fields,
  // though some fields will be null.
  finalHours = finalHours.map(hour => setBusinessOnDeliveryOptionOnRecord(
    hour, HOUR_TYPES.pickup, false,
  ));
  finalHours = setOpenWhenBothTimesPresent(finalHours);

  return finalHours;
};

const putTempRecordInfoIntoLeastRecordsPossible = (
  hourType, existingIdRecords, newRecords, tempRecordsByHourType,
) => {
  const findRecordsWithSpaceArgs = {
    existingIdRecords,
    newRecords,
    hourType,
  };
  const {
    recordsWithSpaceAndExistingId, newRecordsWithSpace,
  } = findRecordsWithSpace(findRecordsWithSpaceArgs);

  const tempRecordsForHourType = tempRecordsByHourType[hourType];
  const mergeParams = {
    tempRecordsForHourType,
    recordsWithSpaceAndExistingId,
    newRecordsWithSpace,
    hourType,
  };
  const {
    updatedExistingRecordsMap,
    updatedNewRecordsMap,
    leftoverTempRecords,
  } = mergeTempRecordsIntoRecordsWithSpace(mergeParams);

  existingIdRecords = collectUnchangedAndUpdatedExistingRecords(
    existingIdRecords, updatedExistingRecordsMap,
  );

  const newRecordsFromLeftoverTempRecords = makeNewRecordsFromLastTempRecords(
    leftoverTempRecords, newRecords,
  );
  newRecords = collectUnchangedAndUpdatedNewRecords(
    newRecords, updatedNewRecordsMap, newRecordsFromLeftoverTempRecords
  );
  return { existingIdRecords, newRecords };
};

const arbitraryDate = [2021, 7, 13];

const putTimeInArbitraryDate = (openOrCloseTimeString) => {
  const [hours, minutes] = openOrCloseTimeString.split(':');
  return new Date(...arbitraryDate, Number(hours), Number(minutes));
};

/**
 * Date-fn's areIntervalsOverlapping and isWithinInterval don't catch the 9:15 in e.g.
 * 9:00 - 9:15, 9:15 - 9:30.
 */
const extendIntervalTenSecondsBothWays = (interval) => {
  const { start, end } = interval;
  const newStart = subSeconds(start, 10);
  const newEnd = addSeconds(end, 10);

  return { start: newStart, end: newEnd };
};

const getHoursOfSameDayAndTab = (allHours, day, hourSettingsKeys) => (
  allHours.filter(hourRecord =>
    hourRecord.day === day && isHourRecordForDeliveryOption(hourRecord, hourSettingsKeys))
);

const summarizeOpenCloseTimes = (hoursOfSameDayAndTab, hourSettingsKeys) => (
  hoursOfSameDayAndTab.map((record) => {
    const { open, close, openAllDay } = hourSettingsKeys;
    const { [open]: openTime, [close]: closeTime, [openAllDay]: isOpenAllDay } = record;
    const { id, tempId } = record;
    const finalId = id || tempId;

    const openTimeAsDate = openTime && putTimeInArbitraryDate(openTime, 'open');
    const closeTimeAsDate = closeTime && putTimeInArbitraryDate(closeTime, 'close');

    const timeProperty = {
      isOpenAllDay,
      ...(!openTimeAsDate && !closeTimeAsDate && { noTimes: true }),
      ...(openTimeAsDate && !closeTimeAsDate && { time: openTimeAsDate }),
      ...(!openTimeAsDate && closeTimeAsDate && { time: closeTimeAsDate }),
      ...(openTimeAsDate && closeTimeAsDate && {
        interval: { start: openTimeAsDate, end: closeTimeAsDate },
      }),
    };

    return { id: finalId, ...timeProperty };
  })
);

const checkIntervalForOverlap = (interval, isOpenAllDay, noTimes) => {
  if (isOpenAllDay || noTimes) return false;
  if (interval) {
    return isIntervalInvalid(interval);
  }
  return false;
};

const checkTimesForOverlaps = ({
  interval1,
  interval2,
  time1,
  time2,
  isOpenAllDay1,
  isOpenAllDay2,
  noTimes1,
  noTimes2,
}) => {
  if (isOpenAllDay1 || isOpenAllDay2 || noTimes1 || noTimes2) return false;
  const bothAreSingleTimesNotIntervals = time1 && time2;
  if (bothAreSingleTimesNotIntervals) return false;
  if (interval1 || interval2) {
    const isAnIntervalInvalid = [interval1, interval2].some(isIntervalInvalid);
    if (isAnIntervalInvalid) return true;
  }

  if ((time1 && interval2) || (time2 && interval1)) {
    const timeToUse = time1 || time2;
    const intervalToUse = interval1 || interval2;
    const extendedInterval = extendIntervalTenSecondsBothWays(intervalToUse);
    return isWithinInterval(timeToUse, extendedInterval);
  } else if (interval1 && interval2) {
    const extendedInterval1 = extendIntervalTenSecondsBothWays(interval1);
    const extendedInterval2 = extendIntervalTenSecondsBothWays(interval2);
    return areIntervalsOverlapping(extendedInterval1, extendedInterval2);
  }
  return false;
};

const isIntervalInvalid = (interval) => {
  if (!interval) return false;
  const { start, end } = interval;
  const isStartBeforeEnd = isBefore(start, end);
  return !isStartBeforeEnd;
};

const saveOverlapsInMap = (openCloseTimesOfRecords, tab) => {
  const overlapResults = {};
  const checkedPairs = {};

  for (const outerInterval of openCloseTimesOfRecords) {
    const { id: id1, interval: interval1, isOpenAllDay: isOpenAllDay1 } = outerInterval;
    const { noTimes: noTimes1, time: time1 } = outerInterval;

    for (const innerInterval of openCloseTimesOfRecords) {
      const { id: id2, interval: interval2, isOpenAllDay: isOpenAllDay2 } = innerInterval;
      const { noTimes: noTimes2, time: time2 } = innerInterval;
      let timesOverlap = false;
      if (checkedPairs[id1] !== id2) {
        if (id1 === id2) {
          timesOverlap = checkIntervalForOverlap(interval1, isOpenAllDay1, noTimes1);
        }

        if (id1 !== id2) {
          const checkOverlapsArgs = {
            interval1,
            interval2,
            time1,
            time2,
            isOpenAllDay1,
            isOpenAllDay2,
            noTimes1,
            noTimes2,
          };
          timesOverlap = checkTimesForOverlaps(checkOverlapsArgs);
        }

        update(overlapResults, `${id1}`, (resultArr = []) => { resultArr.push(timesOverlap); return resultArr; });
        update(overlapResults, `${id2}`, (resultArr = []) => { resultArr.push(timesOverlap); return resultArr; });

        checkedPairs[id2] = id1;
      }
    }
  }

  const finalOverlapResults = mapValues(overlapResults, (resultArr) => {
    const hasOverlap = resultArr.some(pairResult => pairResult);
    return { [tab]: hasOverlap };
  });

  return finalOverlapResults;
};

const getOverlapsOnSameDayAndTab = ({
  tab,
  allHourRecords,
  day,
  overlappingRecordIds,
}) => {
  const hourSettingsKeys = HOUR_SETTINGS[tab];
  const hoursOfSameDayAndTab = getHoursOfSameDayAndTab(allHourRecords, day, hourSettingsKeys);

  const openCloseTimesOfRecords = summarizeOpenCloseTimes(hoursOfSameDayAndTab, hourSettingsKeys);
  const overlappingHourRecordIds = saveOverlapsInMap(
    openCloseTimesOfRecords,
    tab,
    overlappingRecordIds,
  );
  return overlappingHourRecordIds;
};

const getOverlapsOnChangeHour = ({
  changedHour,
  allHourRecords,
  tab,
  overlappingRecordIds,
}) => {
  const { day } = changedHour;
  const checkOverlapsArgs = {
    tab,
    allHourRecords,
    day,
    overlappingRecordIds,
  };
  const newOverlaps = getOverlapsOnSameDayAndTab(checkOverlapsArgs);
  const newOverlappingRecordIds = addNewOverlaps(newOverlaps, overlappingRecordIds);

  return newOverlappingRecordIds;
};

const isAnyHoursOverlapping = (allHourRecords) => {
  for (const day of Constants.SEVEN_DAYS_IN_WEEK) {
    for (const hourType of HOUR_TYPES_ARRAY) {
      const checkOverlapsArgs = {
        tab: hourType,
        allHourRecords,
        day,
        overlappingRecordIds: {},
      };
      const newOverlaps = getOverlapsOnSameDayAndTab(checkOverlapsArgs);
      // e.g. [ { delivery: true }, { delivery: false } ]
      const overlapStatuses = Object.values(newOverlaps);
      const hasOverlaps = overlapStatuses.some(status => status[hourType]);
      if (hasOverlaps) return true;
    }
  }
  return false;
};

const getHasOverlapsSummary = (allHourRecords) => {
  const tabsWithOverlaps = [];
  let hoursHaveOverlaps = false;
  for (const day of Constants.SEVEN_DAYS_IN_WEEK) {
    for (const hourType of HOUR_TYPES_ARRAY) {
      if (hourType === HOUR_TYPES.pickup) break;
      const checkOverlapsArgs = {
        tab: hourType,
        allHourRecords,
        day,
        overlappingRecordIds: {},
      };
      const newOverlaps = getOverlapsOnSameDayAndTab(checkOverlapsArgs);
      // e.g. [ { delivery: true }, { delivery: false } ]
      const overlapStatuses = Object.values(newOverlaps);
      const hasOverlaps = overlapStatuses.some(status => status[hourType]);

      if (hasOverlaps) {
        tabsWithOverlaps.push([hourType, true]);
        hoursHaveOverlaps = true;
      }
    }
  }
  return { tabsWithOverlaps, hoursHaveOverlaps };
};

const getAllOverlappingHours = (allHourRecords, currentOverlappingRecordIds) => {
  let overlappingRecordIds = currentOverlappingRecordIds;
  for (const day of Constants.SEVEN_DAYS_IN_WEEK) {
    for (const hourType of HOUR_TYPES_ARRAY) {
      const checkOverlapsArgs = {
        tab: hourType,
        allHourRecords,
        day,
        overlappingRecordIds,
      };
      const newOverlaps = getOverlapsOnSameDayAndTab(checkOverlapsArgs);
      overlappingRecordIds = addNewOverlaps(newOverlaps, overlappingRecordIds);
    }
  }
  return overlappingRecordIds;
};

const addNewOverlaps = (overlaps, currentOverlaps) => {
  forOwn(overlaps, (overlapsForHourId, hourId) => {
    update(currentOverlaps, `${hourId}`, (currentTabsWithOverlap = {}) => {
      const newOverlaps = Object.assign(currentTabsWithOverlap, overlapsForHourId);
      return newOverlaps;
    });
  });
  return currentOverlaps;
};

const getIdOfRecord = hourRecord => hourRecord.id || hourRecord.tempId;

const isTempRecordForDeliveryOption = (hour, deliveryOption) => {
  const {
    [HOUR_SETTINGS[deliveryOption].closed]: closed,
    [HOUR_SETTINGS[deliveryOption].open]: open,
    [HOUR_SETTINGS[deliveryOption].close]: close,
    [HOUR_SETTINGS[deliveryOption].openAllDay]: openAllDay,
  } = hour;
  const hasValuesForTab = !isUndefined(closed)
    || !isUndefined(open) || !isUndefined(close) || !isUndefined(openAllDay);
  return hour.tempId && hasValuesForTab;
};

const isExistingRecord = hour => !!hour.id;
const hasClearedBusinessHours = (hour, shouldCheckIsExistingRecord) => {
  const {
    [HOUR_SETTINGS[HOUR_TYPES.business].open]: open,
    [HOUR_SETTINGS[HOUR_TYPES.business].close]: close,
    [HOUR_SETTINGS[HOUR_TYPES.business].closed]: closed,
    [HOUR_SETTINGS[HOUR_TYPES.business].openAllDay]: openAllDay,
  } = hour;

  const isExistingRecordValue = shouldCheckIsExistingRecord ? isExistingRecord(hour) : true;

  return isExistingRecordValue && isNull(open) && isNull(close) && isNull(closed) && !openAllDay;
};

const getUniqueId = () => uuidv4();

const addTempRecordToDeliveryOption = (hour, newTempRecordsForDeliveryOption, deliveryOption) => {
  const newTempRecord = {};
  const businessClosed = hour[HOUR_SETTINGS[HOUR_TYPES.business].closed];
  const closedValue = deliveryOption === HOUR_TYPES.pickup && businessClosed === null
    ? false
    : businessClosed;
  const BUSINESS_HOURS = HOUR_SETTINGS[HOUR_TYPES.business];
  newTempRecord[HOUR_SETTINGS[deliveryOption].open] = hour[BUSINESS_HOURS.open];
  newTempRecord[HOUR_SETTINGS[deliveryOption].close] = hour[BUSINESS_HOURS.close];
  newTempRecord[HOUR_SETTINGS[deliveryOption].closed] = closedValue;
  newTempRecord[HOUR_SETTINGS[deliveryOption].openAllDay] = hour[BUSINESS_HOURS.openAllDay];
  newTempRecord.day = hour.day;
  newTempRecord.tempId = getUniqueId();

  newTempRecordsForDeliveryOption.push(newTempRecord);
  return newTempRecordsForDeliveryOption;
};

const hasBusinessHours = (hour, shouldCheckIsExistingRecord) => {
  const {
    [HOUR_SETTINGS[HOUR_TYPES.business].open]: open,
    [HOUR_SETTINGS[HOUR_TYPES.business].close]: close,
  } = hour;

  const isExistingRecordValue = shouldCheckIsExistingRecord ? isExistingRecord(hour) : true;

  return isExistingRecordValue && !!open && !!close;
};

const setBusinessOnDeliveryOptionOnRecord = (hour, deliveryOption, checkIsExistingRecord) => {
  const hourClone = { ...hour };
  const businessClosedValue = hourClone[HOUR_SETTINGS[HOUR_TYPES.business].closed];
  const pickupsClosedValue = hasClearedBusinessHours(hour, checkIsExistingRecord)
    || hasBusinessHours(hour, checkIsExistingRecord)
    ? false
    : businessClosedValue;
  const deliveryClosedValue = businessClosedValue;

  const closedValue = deliveryOption === HOUR_TYPES.pickup
    ? pickupsClosedValue
    : deliveryClosedValue;

  const BUSINESS_HOURS = HOUR_SETTINGS[HOUR_TYPES.business];
  hourClone[HOUR_SETTINGS[deliveryOption].open] = hourClone[BUSINESS_HOURS.open];
  hourClone[HOUR_SETTINGS[deliveryOption].close] = hourClone[BUSINESS_HOURS.close];
  hourClone[HOUR_SETTINGS[deliveryOption].closed] = closedValue;
  hourClone[HOUR_SETTINGS[deliveryOption].openAllDay] = hourClone[BUSINESS_HOURS.openAllDay];
  return hourClone;
};

const setBusinessHoursOnDeliveryOption = (hours, deliveryOption, nonTargetDeliveryOption) => {
  let newTempRecordsForDeliveryOption = [];

  const newHours = hours.map((hour) => {
    if (isTempRecordForDeliveryOption(hour, deliveryOption)) return null;
    if (isTempRecordForDeliveryOption(hour, nonTargetDeliveryOption)) return hour;
    if (isTempRecordForDeliveryOption(hour, HOUR_TYPES.business)) {
      newTempRecordsForDeliveryOption = addTempRecordToDeliveryOption(
        hour,
        newTempRecordsForDeliveryOption,
        deliveryOption,
      );
      return hour;
    }

    if (isExistingRecord(hour)) {
      return setBusinessOnDeliveryOptionOnRecord(hour, deliveryOption, true);
    }
  });

  const nullsRemoved = newHours.filter(hour => !!hour);
  return [...nullsRemoved, ...newTempRecordsForDeliveryOption];
};

const initializeDayWithTempRecords = (dayName, tempIdCounter) => {
  const businessTemp = {
    [HOUR_SETTINGS[HOUR_TYPES.business].open]: null,
    [HOUR_SETTINGS[HOUR_TYPES.business].close]: null,
    [HOUR_SETTINGS[HOUR_TYPES.business].closed]: false,
    [HOUR_SETTINGS[HOUR_TYPES.business].openAllDay]: false,
    day: dayName,
    tempId: tempIdCounter,
  };
  const deliveryTemp = {
    [HOUR_SETTINGS[HOUR_TYPES.delivery].open]: null,
    [HOUR_SETTINGS[HOUR_TYPES.delivery].close]: null,
    [HOUR_SETTINGS[HOUR_TYPES.delivery].closed]: true,
    [HOUR_SETTINGS[HOUR_TYPES.delivery].openAllDay]: false,
    day: dayName,
    tempId: tempIdCounter - 1,
  };
  const pickupTemp = {
    [HOUR_SETTINGS[HOUR_TYPES.pickup].open]: null,
    [HOUR_SETTINGS[HOUR_TYPES.pickup].close]: null,
    [HOUR_SETTINGS[HOUR_TYPES.pickup].closed]: false,
    [HOUR_SETTINGS[HOUR_TYPES.pickup].openAllDay]: false,
    day: dayName,
    tempId: tempIdCounter - 2,
  };

  const tempRecords = [businessTemp, deliveryTemp, pickupTemp];
  const newTempIdCounter = tempIdCounter - 3;
  return { tempRecords, newTempIdCounter };
};

const setPrevDeliveryHours = (prevHours, newHours) => {
  const deliveryHoursFields = [
    HOUR_SETTINGS[HOUR_TYPES.delivery].closed,
    HOUR_SETTINGS[HOUR_TYPES.delivery].close,
    HOUR_SETTINGS[HOUR_TYPES.delivery].open,
    HOUR_SETTINGS[HOUR_TYPES.delivery].openAllDay,
  ];

  return newHours.map((hour, index) => {
    const hourClone = cloneDeep(hour);
    deliveryHoursFields.forEach((field) => {
      hourClone[field] = prevHours[index][field];
    });
    return hourClone;
  });
};

export {
  addNewOverlaps,
  getAllOverlappingHours,
  getHasOverlapsSummary,
  getIsHourValidResults,
  getOverlapsOnSameDayAndTab,
  getOverlapsOnChangeHour,
  consolidateHoursRecords,
  isAnyHoursOverlapping,
  ERROR_MESSAGE,
  getIdOfRecord,
  getLeadingZeroTime,
  getLeadingZeroTimes,
  getUniqueId,
  HOUR_ERROR_TYPES,
  HOUR_SETTINGS,
  HOUR_TYPES,
  HOUR_TYPES_ARRAY,
  HOUR_VALID_FORMS,
  initializeDayWithTempRecords,
  initializeHours,
  isHourRecordForDeliveryOption,
  isHourObjectValid,
  isHourTypeEmpty,
  isRecordOverwriteable,
  isSettingsInHourValid,
  isValidHourString,
  isAllHourTypesValid,
  removeCompletelyEmptyRecords,
  setBusinessHoursOnDeliveryOption,
  setBusinessOnDeliveryOptionOnRecord,
  setOpenWhenBothTimesPresent,
  setPrevDeliveryHours,
  validateOneHourTypeOfOneRecord,
};
